import { useMemo, useState } from 'react';
import { Badge, Container, Group, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { useTitleRunsList } from '../../queries/title-runs';
import type { TitleRunStatus } from '../../types/api';

const STATUS_OPTIONS: { value: TitleRunStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'under_review', label: 'Under review' },
  { value: 'complete', label: 'Complete' },
];

export function TitleRunsListPage() {
  const [status, setStatus] = useState<TitleRunStatus | ''>('');
  const filters = useMemo(() => (status ? { status } : {}), [status]);
  const { data: runs = [], isLoading, isError } = useTitleRunsList(filters);
  const navigate = useNavigate();

  return (
    <Container size="xl">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Stack gap={0}>
            <Title order={2}>Title Runs</Title>
            {runs.length > 0 && (
              <Badge variant="light" color="pine" size="sm">
                {runs.length} run{runs.length === 1 ? '' : 's'}
              </Badge>
            )}
          </Stack>
          <Select
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setStatus((v ?? '') as TitleRunStatus | '')}
            w={200}
          />
        </Group>

        <Paper withBorder radius="md">
          <DataTable
            records={runs}
            fetching={isLoading}
            minHeight={200}
            noRecordsText={isError ? 'Failed to load' : 'No title runs yet'}
            highlightOnHover
            onRowClick={({ record }) => navigate(`/title-runs/${record.id}`)}
            columns={[
              {
                accessor: 'title',
                title: 'Title',
                render: (r) => (
                  <Link to={`/title-runs/${r.id}`} style={{ textDecoration: 'none' }}>
                    {r.title}
                  </Link>
                ),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 140,
                render: (r) => <Badge variant="light">{r.status.replace('_', ' ')}</Badge>,
              },
              {
                accessor: 'sovereignty_date',
                title: 'Sovereignty',
                width: 120,
                render: (r) => r.sovereignty_date ?? '—',
              },
              {
                accessor: 'through_date',
                title: 'Through',
                width: 120,
                render: (r) => r.through_date ?? '—',
              },
              {
                accessor: 'updated_at',
                title: 'Updated',
                width: 160,
                render: (r) => (
                  <Text size="sm">{dayjs(r.updated_at).format('YYYY-MM-DD HH:mm')}</Text>
                ),
              },
            ]}
          />
        </Paper>
      </Stack>
    </Container>
  );
}
